import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { initMetaPixel, trackPageView } from '@/lib/meta-pixel';
import { useTrackingParams } from '@/hooks/useTrackingParams';
import { captureAttribution } from '@/lib/attribution';

/**
 * Initializes the Meta Pixel and fires PageView events on every
 * route change. Also captures UTM parameters and fbclid from URLs.
 *
 * Place this inside a <BrowserRouter> to get route-change tracking.
 * If VITE_META_PIXEL_ID is not set, all tracking is silently skipped.
 *
 * Two capture calls, deliberately: `useTrackingParams` feeds Meta CAPI via its
 * sessionStorage `_fbc` synthesis, while `captureAttribution` (#3302) persists
 * first/last touch to the cookies shared with the marketing site. Different
 * lifetimes, different consumers — see src/lib/attribution.ts.
 */
export function MetaPixelProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  // Capture UTM + fbclid params (sessionStorage; feeds Meta CAPI)
  useTrackingParams();

  // Persist first/last-touch attribution to the cookies shared with the
  // marketing site (#3302). Re-runs on every search-param change so a param
  // picked up mid-session still lands as a last touch.
  useEffect(() => {
    captureAttribution();
  }, [location.search]);

  // Initialize Pixel once
  useEffect(() => {
    initMetaPixel();
  }, []);

  // Track PageView on route changes (skip duplicate fires for same path)
  useEffect(() => {
    if (location.pathname !== previousPath.current) {
      trackPageView();
      previousPath.current = location.pathname;
    }
  }, [location.pathname]);

  return <>{children}</>;
}
